import { useId, useState } from 'react'
import { fbt } from 'fbtee'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Switch } from './ui/switch'
import { DatePicker } from './ui/date-picker'
import { useClock } from '../hooks/useClock'
import { toDateISO, toTimeHM } from '../lib/formatDate'

// The date/time editor for the global clock override. Seeds from the current
// effective time on mount (so it re-seeds each time the host popover opens), sets
// or clears the override, and calls `onDone` so the host can close. Hosted by the
// header-logo popover.
export function ClockOverrideFields({ onDone }: { onDone?: () => void }) {
  const { now, override, setOverride, simulating, setSimulating } = useClock()
  const dateId = useId()
  const timeId = useId()
  const simId = useId()

  const seed = override ?? now
  const [dateStr, setDateStr] = useState(() => toDateISO(seed))
  const [timeStr, setTimeStr] = useState(() => toTimeHM(seed))

  function apply() {
    const combined = new Date(`${dateStr}T${timeStr || '00:00'}:00`)
    if (Number.isNaN(combined.getTime())) return
    setOverride(combined)
    onDone?.()
  }

  function reset() {
    setOverride(null)
    onDone?.()
  }

  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor={dateId}>
          {fbt('Date', 'Label for the date field of the clock override')}
        </Label>
        <DatePicker id={dateId} value={dateStr} onChange={setDateStr} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={timeId}>
          <fbt desc="Label for the time field of the clock override">Time</fbt>
        </Label>
        <Input id={timeId} type="time" value={timeStr} onChange={e => setTimeStr(e.target.value)} />
      </div>
      <div className="flex items-center justify-between gap-3 rounded-md border p-2.5">
        <Label htmlFor={simId} className="flex flex-col gap-0.5 font-normal">
          <span className="font-medium">
            <fbt desc="Toggle label to fast-forward the simulated clock">Advance time</fbt>
          </span>
          <span className="text-xs text-muted-foreground">
            <fbt desc="Description of the fast-forward clock simulation rate">
              5 minutes every second
            </fbt>
          </span>
        </Label>
        <Switch id={simId} checked={simulating} onCheckedChange={setSimulating} />
      </div>
      <div className="flex gap-2 pt-1">
        <Button className="flex-1" size="sm" onClick={apply}>
          <fbt desc="Button to apply the clock override">Set time</fbt>
        </Button>
        <Button variant="outline" size="sm" className="flex-1" disabled={!override} onClick={reset}>
          <fbt desc="Button to clear the clock override and return to live time">Reset to live</fbt>
        </Button>
      </div>
    </>
  )
}

